// import { useEffect, useState } from 'react';
// import { Participant } from 'twilio-video';

// export default function useParticipantIsReconnecting(participant: Participant) {
//   const [isReconnecting, setIsReconnecting] = useState(false);

//   useEffect(() => {
//     const handleReconnecting = () => setIsReconnecting(true);
//     const handleReconnected = () => setIsReconnecting(false);

//     handleReconnected(); // Reset state when there is a new participant

//     participant.on('reconnecting', handleReconnecting);
//     participant.on('reconnected', handleReconnected);
//     return () => {
//       participant.off('reconnecting', handleReconnecting);
//       participant.off('reconnected', handleReconnected);
//     };
//   }, [participant]);

//   return isReconnecting;
// }
import { useEffect, useState, useRef } from 'react';
import { Participant, LocalVideoTrack, LocalAudioTrack } from 'twilio-video';

// Define the interface for local participant methods
interface LocalParticipantLike extends Participant {
  publishTrack: (track: any) => any;
  videoTracks: Map<string, any>;
  audioTracks: Map<string, any>;
}

export default function useParticipantIsReconnecting(participant: Participant) {
  const [isReconnecting, setIsReconnecting] = useState(false);
  const previousTracksState = useRef<{
    videoEnabled: boolean;
    audioEnabled: boolean;
  }>({ videoEnabled: false, audioEnabled: false });

  useEffect(() => {
    const handleReconnecting = async () => {
      setIsReconnecting(true);

      // Check if this is a local participant by checking for local methods/properties
      if (participant && 'publishTrack' in participant) {
        const localParticipant = participant as LocalParticipantLike;

        // Store current state before disabling
        const videoTrack = Array.from(localParticipant.videoTracks.values())[0]?.track as LocalVideoTrack;
        const audioTrack = Array.from(localParticipant.audioTracks.values())[0]?.track as LocalAudioTrack;

        previousTracksState.current = {
          videoEnabled: videoTrack?.isEnabled || false,
          audioEnabled: audioTrack?.isEnabled || false,
        };

        // Disable camera and microphone
        if (videoTrack) {
          videoTrack.disable();
        }
        if (audioTrack) {
          audioTrack.disable();
        }

        console.log('Internet disconnected: Camera and microphone disabled');
      }
    };

    const handleReconnected = async () => {
      setIsReconnecting(false);

      // Check if this is a local participant by checking for local methods/properties
      if (participant && 'publishTrack' in participant) {
        const localParticipant = participant as LocalParticipantLike;

        // Wait a brief moment to ensure connection is stable
        setTimeout(() => {
          const videoTrack = Array.from(localParticipant.videoTracks.values())[0]?.track as LocalVideoTrack;
          const audioTrack = Array.from(localParticipant.audioTracks.values())[0]?.track as LocalAudioTrack;

          // Restore previous state
          if (videoTrack && previousTracksState.current.videoEnabled) {
            videoTrack.enable();
          }
          if (audioTrack && previousTracksState.current.audioEnabled) {
            audioTrack.enable();
          }

          console.log('Internet reconnected: Camera and microphone restored');
        }, 500); // Brief delay to ensure stable connection
      }
    };

    // Reset state when there is a new participant
    setIsReconnecting(false);

    participant.on('reconnecting', handleReconnecting);
    participant.on('reconnected', handleReconnected);

    return () => {
      participant.off('reconnecting', handleReconnecting);
      participant.off('reconnected', handleReconnected);
    };
  }, [participant]);

  return isReconnecting;
}
